import React from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Autocomplete from '@mui/material/Autocomplete';
import { Close, Delete, Add } from '@mui/icons-material';
import { formStyles } from '../../util/styles';
import { generateObjectIdLike } from '../../util/objectId';
import { createTemplate, updateTemplate } from '../../db/sync/templateApi';
import ScoreDefinitionsEditor from '../../components/Demographics/ScoreDefinitionsEditor';

const emptyTemplate = () => ({
  name: '',
  categoryTemplates: [],
  scoreDefinitions: [],
  scoringRules: [],
});

const EditModal = ({ open, onClose, template, onSaved }) => {
  const classes = formStyles();
  const [info, setInfo] = React.useState(emptyTemplate());
  const [errors, setErrors] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setInfo(template ? {
        name: template.name || '',
        categoryTemplates: template.categoryTemplates ? [...template.categoryTemplates] : [],
        scoreDefinitions: template.scoreDefinitions ? [...template.scoreDefinitions] : [],
        scoringRules: template.scoringRules ? [...template.scoringRules] : [],
      } : emptyTemplate());
      setErrors([]);
      setSaveError(null);
    }
  }, [open, template]);

  const isEditing = !!(template && template._id);

  // --- Categories ---------------------------------------------------------
  const addCategory = () => {
    setInfo((prev) => ({
      ...prev,
      categoryTemplates: [
        ...prev.categoryTemplates,
        { _id: generateObjectIdLike(), name: '', options: [], allowFreeform: true },
      ],
    }));
  };

  const updateCategory = (categoryId, updates) => {
    setInfo((prev) => ({
      ...prev,
      categoryTemplates: prev.categoryTemplates.map((c) =>
        c._id === categoryId ? { ...c, ...updates } : c),
    }));
  };

  const removeCategory = (categoryId) => {
    setInfo((prev) => {
      const removed = prev.categoryTemplates.find((c) => c._id === categoryId);
      return {
        ...prev,
        categoryTemplates: prev.categoryTemplates.filter((c) => c._id !== categoryId),
        // A rule referencing a deleted category would otherwise become a
        // permanently-inactive "no answer" delta that silently applies to
        // every juror this template is applied to (the category can never
        // have an entry again once it's gone).
        scoringRules: removed
          ? prev.scoringRules.filter((r) => r.categoryName !== removed.name)
          : prev.scoringRules,
      };
    });
  };

  const handleSave = async () => {
    const errorList = [];
    if (!info.name || !info.name.trim()) {
      errorList.push('Template name must be populated');
    }
    for (const category of info.categoryTemplates) {
      if (!category.name || !category.name.trim()) {
        errorList.push('Every category must have a name');
        break;
      }
    }
    for (const score of info.scoreDefinitions) {
      if (!score.name || !score.name.trim()) {
        errorList.push('Every score must have a name');
        break;
      }
    }

    if (errorList.length > 0) {
      setErrors(errorList);
      return;
    }

    const payload = {
      name: info.name.trim(),
      categoryTemplates: info.categoryTemplates.map((c) => ({
        ...c,
        name: c.name.trim(),
      })),
      scoreDefinitions: info.scoreDefinitions.map((s) => ({
        ...s,
        name: s.name.trim(),
        showOnJurorCard: s.showOnJurorCard !== false,
      })),
      scoringRules: info.scoringRules,
    };

    setSaving(true);
    setSaveError(null);
    try {
      const saved = isEditing
        ? await updateTemplate(template._id, payload)
        : await createTemplate(payload);
      setSaving(false);
      onSaved?.(saved);
      onClose();
    } catch (error) {
      setSaving(false);
      const message = error?.response?.data?.error || error.message || 'Failed to save template';
      setSaveError(message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {isEditing ? 'Edit Template' : 'Create Template'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Collapse in={errors.length > 0}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.map((error) => <li key={error}>{error}</li>)}
            </ul>
          </Alert>
        </Collapse>
        <Collapse in={!!saveError}>
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        </Collapse>

        <TextField
          size="small"
          fullWidth
          label="Template Name"
          value={info.name}
          onChange={(e) => setInfo({ ...info, name: e.target.value })}
          sx={{ mb: 3 }}
        />

        {/* Categories */}
        <Typography variant="overline" display="block">Categories</Typography>
        {info.categoryTemplates.map((category) => (
          <Grid container spacing={1} alignItems="center" key={category._id} sx={{ mb: 1 }}>
            <Grid size={3}>
              <TextField
                size="small"
                fullWidth
                label="Category name"
                value={category.name}
                onChange={(e) => updateCategory(category._id, { name: e.target.value })}
              />
            </Grid>
            <Grid size={6}>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={[]}
                value={category.options || []}
                onChange={(e, newValue) => updateCategory(category._id, {
                  options: newValue.map((v) => v.trim()).filter(Boolean),
                })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Predefined options" />
                )}
              />
              <Typography variant="caption" color="text.secondary" component="div">
                Press <code>Enter</code> to create an option
              </Typography>
            </Grid>
            <Grid size={2}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={category.allowFreeform !== false}
                    onChange={(e) => updateCategory(category._id, { allowFreeform: e.target.checked })}
                  />
                }
                label="Freeform"
              />
            </Grid>
            <Grid size={1}>
              <IconButton size="small" onClick={() => removeCategory(category._id)}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button size="small" startIcon={<Add />} onClick={addCategory}>Add category</Button>

        {/* Scores + nested scoring rules */}
        <Box sx={{ mt: 3 }}>
          <ScoreDefinitionsEditor
            categories={info.categoryTemplates.map((c) => ({ name: c.name, options: c.options || [] }))}
            scoreDefinitions={info.scoreDefinitions}
            scoringRules={info.scoringRules}
            onChange={(scoreDefinitions, scoringRules) =>
              setInfo((prev) => ({ ...prev, scoreDefinitions, scoringRules }))}
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={onClose} color="primary">Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModal;
